import axios from 'axios';
import {message} from "antd";
import {clearAuthSession} from "./auth-session";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access-token');
        const workSpaceId = localStorage.getItem('workspace-id')
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        if (workSpaceId) {
            config.headers['X-Workspace-Id'] = workSpaceId;
        }

        // No request/response logging here. The config carries the operator's bearer token and the
        // response carries customers' names, phones and messages; both were written to the browser
        // console on every call in production, where a screen-share, a shared PC or an extension
        // could read them. Debug with the Network tab, which the operator controls.
        return config;
    },
    (error) => {
        // The AxiosError object embeds the request config (bearer token) — log a summary only.
        console.error('Request Error:', error?.message);
        return Promise.reject(error);
    }
);

api.interceptors.response.use(
    (response) => {
        return response.data;
    },
    async (error) => {
        // Same: the raw AxiosError carries config.headers.Authorization and the response body.
        console.error('Response Error:', error?.config?.method, error?.config?.url, error?.response?.status, error?.message);

        // 403 means "you may not do THIS", not "your session is over".
        //
        // This used to sign the operator out and hard-navigate to /login on any 403 from any call.
        // That was always the wrong reading, and it became actively dangerous once refusals turned
        // into ordinary answers: declining to edit a message that is not yours, or a conversation
        // that is not yours, would eject an operator from the console in the middle of a shift and
        // lose whatever they were typing. The caller shows the server's explanation instead; only a
        // failed token renewal (below) ends a session.
        if (error.response && error.response.status === 403) {
            console.warn('[api] refused:', error.config?.url, error.response?.data?.message);
        }

        if (error.response && error.response.status === 401) {
            try {
                const refreshToken = localStorage.getItem("refresher-token");
                if (!refreshToken) {
                    // Missing token: there is genuinely no session to renew. `return` matters —
                    // without it this fell through and attempted the renewal anyway, with
                    // `Bearer undefined`.
                    message.error("نشست شما پایان یافته است. دوباره وارد شوید.");
                    clearAuthSession();
                    window.location.href = '/login?reason=session-expired';
                    return Promise.reject(error);
                }
                const refreshResponse = await axios.post(
                    BASE_URL + '/refresh', {
                        refreshToken: `Bearer ${refreshToken}`
                    }
                );
                localStorage.setItem("access-token", refreshResponse?.data?.accessToken);
                // The refresh token now rotates on every renewal so a long shift never expires
                // mid-conversation. Not storing it here would leave this path handing back the
                // original token until it aged out — the exact ceiling that stranded an operator.
                if (refreshResponse?.data?.refreshToken) {
                    localStorage.setItem("refresher-token", refreshResponse.data.refreshToken);
                }
                error.config.headers['Authorization'] = `Bearer ${refreshResponse?.data?.accessToken}`;
                return api(error.config);
            } catch (refreshError) {
                // Ending the session is only correct when the SERVER said the token is no longer
                // good. This used to eject on any failure at all, so a renewal that simply never
                // reached the server — a few seconds of bad wifi — threw the operator out of the
                // console mid-conversation and onto the login screen. That is exactly what was
                // reported, and the server's log agrees: every /refresh that arrived that day
                // returned 200. The ones that ended the session never arrived.
                //
                // /refresh answers 503, not 401, for its own faults precisely so that this
                // distinction can be made here.
                const serverRejectedTheToken =
                    refreshError?.response &&
                    [401, 403].includes(refreshError.response.status);

                if (serverRejectedTheToken) {
                    console.warn('[api] refresh rejected — ending session');
                    // Clear BEFORE navigating. /login now forwards anyone who still has tokens to
                    // their dashboard, so arriving there with the dead pair still stored meant:
                    // forward, first request refused, back here, forward — once a second, for as
                    // long as the tab stayed open.
                    clearAuthSession();
                    window.location.href = '/login?reason=session-expired';
                } else {
                    // Keep them signed in. The renewal timer will try again shortly, and the caller
                    // gets a normal failure it can report and retry rather than losing the console.
                    console.warn('[api] refresh could not be delivered — keeping the session', refreshError?.message);
                }
            }
        }

        return Promise.reject(error);
    }
);

export default api;